import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { Tabs } from 'components/Tabs/Tabs'
import { TabProvider, useTabContext } from 'components/Tabs/TabContext'
import {
  Button,
  ColorVariant,
  Flex,
  MainHeadline,
  PageWrapper,
} from 'styling/GlobalStyles'
import { NormalText } from 'styling/TypographyV2'
import { AppDispatch } from 'store'
import { clearOperationalManagement } from 'features/operationalManagement/operationalManagementSlice'
import { ExportIcon } from 'assets/icons/ExportIcon'
import { PeopleGroupIcon } from './icons'
import { OrganogramHeader } from './Organogram.styled'
import { OverviewTab } from './tabs/OverviewTab'
import { TeamsTab } from './tabs/TeamsTab'
import { OrgStructureTab } from './tabs/OrgStructureTab'
import { AssignmentsTab } from './tabs/AssignmentsTab'
import { AuditLogTab } from './tabs/AuditLogTab'
import { CreateTeamModal } from './components/CreateTeamModal'
import { CreateAssignmentModal } from './components/CreateAssignmentModal'
import { BulkAssignWizard } from './BulkAssign/BulkAssignWizard'

const TAB_NAMES = [
  'Overview',
  'Org Structure',
  'Teams',
  'Assignments',
  'Audit Log',
] as const

const TabBody = ({
  teamsRefreshKey,
  assignmentsRefreshKey,
}: {
  teamsRefreshKey: number
  assignmentsRefreshKey: number
}) => {
  const { activeTab } = useTabContext()
  switch (activeTab) {
    case 'Overview':
      return <OverviewTab />
    case 'Org Structure':
      return <OrgStructureTab />
    case 'Teams':
      return <TeamsTab refreshKey={teamsRefreshKey} />
    case 'Assignments':
      return <AssignmentsTab refreshKey={assignmentsRefreshKey} />
    case 'Audit Log':
      return <AuditLogTab />
    case null:
    case undefined:
      return null
    default:
      return null
  }
}

const HeaderContent = ({
  onCreateTeam,
  onCreateAssignment,
  onBulkAssign,
}: {
  onCreateTeam: () => void
  onCreateAssignment: () => void
  onBulkAssign: () => void
}) => {
  const { activeTab } = useTabContext()
  const isAssignments = activeTab === 'Assignments'

  const subtitle = isAssignments
    ? 'Global view of all operational assignments'
    : 'Operational structure overview'

  return (
    <OrganogramHeader>
      <Flex column gap='0.25rem'>
        <MainHeadline>Organogram</MainHeadline>
        <NormalText semibold colorVariant={ColorVariant.BASE}>
          {subtitle}
        </NormalText>
      </Flex>
      <Flex gap='0.75rem' wrap>
        {isAssignments ? (
          <>
            <Button orangeBorder width='150'>
              <Flex center gap='0.5rem'>
                <span style={{ display: 'inline-flex' }}>{ExportIcon}</span>
                Export list
              </Flex>
            </Button>
            <Button orangeBorder width='150' onClick={onBulkAssign}>
              <Flex center gap='0.5rem'>
                <span style={{ display: 'inline-flex' }}>
                  <PeopleGroupIcon size={16} />
                </span>
                Bulk Assign
              </Flex>
            </Button>
            <Button orange width='180' onClick={onCreateAssignment}>
              Create assignment
            </Button>
          </>
        ) : (
          <Button orange onClick={onCreateTeam}>
            Create Team
          </Button>
        )}
      </Flex>
    </OrganogramHeader>
  )
}

export const Organogram = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [createTeamOpen, setCreateTeamOpen] = useState(false)
  const [createAssignmentOpen, setCreateAssignmentOpen] = useState(false)
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [teamsRefreshKey, setTeamsRefreshKey] = useState(0)
  const [assignmentsRefreshKey, setAssignmentsRefreshKey] = useState(0)

  useEffect(() => {
    return () => {
      dispatch(clearOperationalManagement())
    }
  }, [dispatch])

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <TabProvider tabs={[...TAB_NAMES]} groupName='tab'>
          {bulkAssignOpen ? (
            <BulkAssignWizard
              onClose={() => {
                setBulkAssignOpen(false)
                setAssignmentsRefreshKey((k) => k + 1)
              }}
            />
          ) : (
            <>
              <HeaderContent
                onCreateTeam={() => setCreateTeamOpen(true)}
                onCreateAssignment={() => setCreateAssignmentOpen(true)}
                onBulkAssign={() => setBulkAssignOpen(true)}
              />

              <Flex>
                <Tabs />
              </Flex>
              <div style={{ marginTop: '1.25rem' }}>
                <TabBody
                  teamsRefreshKey={teamsRefreshKey}
                  assignmentsRefreshKey={assignmentsRefreshKey}
                />
              </div>
            </>
          )}
        </TabProvider>

        {createTeamOpen && (
          <CreateTeamModal
            onClose={() => setCreateTeamOpen(false)}
            onCreated={() => setTeamsRefreshKey((k) => k + 1)}
          />
        )}

        {createAssignmentOpen && (
          <CreateAssignmentModal
            onClose={() => setCreateAssignmentOpen(false)}
            onCreated={() => setAssignmentsRefreshKey((k) => k + 1)}
          />
        )}
      </PageWrapper>
    </>
  )
}
